import axios from 'axios';
import { omit } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import urlJoin from 'url-join';

import { useConfig } from '../../../../_components/config';
import { useToastr } from '../../../../_components/toastr';
import type { Session } from '../../../../_components/use-check-session';
import type { SubmissionResult } from '../submission';

import type { WizardFormType } from './use-steps';

export interface GetUploadUrlRequest {
  contentType: string;
  contentLength: number;
}
export interface GetUploadUrlResponse {
  url: string;
  key: string;
}

export const useSubmitSteps = (sessionId: string, session: Session) => {
  const [submitted, setSubmitted] = useState(false);
  const [request, setSubmittedRequest] = useState<SubmissionResult>(
    {} as SubmissionResult
  );
  const { api } = useConfig();
  const toast = useToastr();
  const { mutateAsync: submit, isLoading: submitLoading } = useMutation({
    mutationFn: async (d: WizardFormType) =>
      axios({
        url: urlJoin(api, 'submit'),
        method: 'post',
        withCredentials: true,
        data: {
          formType: session?.formType,
          email: session?.email,
          formData: d,
          browserTime: new Date(), // track the user's browser time, for auditing/troubleshooting
          browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // API will convert to standardized time zone AND used for auditing.
        },
      }),
    onSuccess: ({ data }: any) => {
      setSubmitted(true);
      setSubmittedRequest(data);
    },
    onError: (err: string) => {
      toast.error(<span>Failed to submit</span>, {
        details: err,
      });
    },
  });

  const onSubmitSteps = useCallback(
    async (data: WizardFormType) => {
      const removeUploaded = data.attachments.map((m) => omit(m, ['uploaded']));
      const cleanData = {
        ...data,
        attachments: removeUploaded,
        veteranIcn: data.veteranIcn?.toUpperCase(),
        veteranReferralId: data.veteranReferralId?.toUpperCase(),
      };
      await submit(cleanData);
    },
    [submit]
  );

  const isSubmitting = submitLoading;
  return {
    submitted,
    request,
    onSubmitSteps,
    isSubmitting,
    setSubmitted,
  };
};
